import { Link } from "react-router-dom";
import {
  CheckCircle,
  Truck,
  PieChart,
  Smartphone,
  Wallet,
  Clock4,
  ArrowRight,
  DollarSign,
  Users,
  FileText,
  Globe,
  UserPlus,
  FileCheck,
  Headphones,
  Star,
} from "lucide-react";
import { useEffect, useRef } from "react";

const BecomePartner = () => {
  const eligibilityImageRef = useRef<HTMLImageElement>(null);
  const expectImageRef = useRef<HTMLImageElement>(null);

  // Parallax effect for images
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;

      if (eligibilityImageRef.current) {
        eligibilityImageRef.current.style.transform = `translateY(${
          scrollY * 0.05
        }px)`;
      }
      if (expectImageRef.current) {
        expectImageRef.current.style.transform = `translateY(${
          scrollY * 0.05
        }px)`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="bg-white text-black font-sans">
      {/* Hero */}
      <section className="bg-gradient-to-b from-white to-[#F5F5F5] px-6 py-32 text-center">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight text-black">
            Deliver More. Earn More.
          </h1>
          <p className="text-lg md:text-xl text-[#666666] mb-8 max-w-2xl mx-auto">
            Take full control of your delivery business with FullPlateNow.
            Manage orders, sync menus, and track everything in one place—no
            delivery fees, just profits.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 text-white bg-[#FF5A5F] px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E04F55] transition-all duration-300 shadow-sm hover:shadow-md"
          >
            Become a Partner
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="px-6 py-32 bg-white max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold mb-12 text-center text-black">
          Why Partner with FullPlateNow?
        </h2>
        <div className="grid md:grid-cols-3 gap-10 text-center">
          {[
            {
              icon: <Truck className="w-8 h-8 text-[#20C997] mx-auto mb-4" />,
              title: "Unified Orders",
              desc: "Manage all delivery services in one dashboard—no more app juggling. See every order from DoorDash, Uber Eats, Grubhub, and Postmates in real time.",
            },
            {
              icon: (
                <PieChart className="w-8 h-8 text-[#20C997] mx-auto mb-4" />
              ),
              title: "Powerful Analytics",
              desc: "Get daily, weekly, and monthly reports on earnings, top items, and performance trends across all platforms to grow your business.",
            },
            {
              icon: (
                <Smartphone className="w-8 h-8 text-[#20C997] mx-auto mb-4" />
              ),
              title: "Live Menu Syncing",
              desc: "Sync prices, availability, and promotions across all delivery apps instantly with one update, saving you hours of manual work.",
            },
            {
              icon: <Wallet className="w-8 h-8 text-[#20C997] mx-auto mb-4" />,
              title: "Zero-Risk Pricing",
              desc: "Keep 100% of your base menu prices. We manage pricing with a 50-60% upcharge (paid by customers) to cover our fee, leaving you with no commissions.",
            },
            {
              icon: <Clock4 className="w-8 h-8 text-[#20C997] mx-auto mb-4" />,
              title: "Faster Fulfillment",
              desc: "Receive, track, and fulfill orders faster with real-time updates and notifications, ensuring smooth operations.",
            },
            {
              icon: (
                <CheckCircle className="w-8 h-8 text-[#20C997] mx-auto mb-4" />
              ),
              title: "Easy Onboarding",
              desc: "Our team works with you directly—setup takes minutes, not days, with personalized support to get you started.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
            >
              {item.icon}
              <h3 className="text-lg font-semibold mb-2 text-black">
                {item.title}
              </h3>
              <p className="text-sm text-[#666666]">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Eligibility Section */}
      <section className="px-6 py-32 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center text-black">
            Who Can Become a Partner?
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#666666] mb-6">
                FullPlateNow is designed for independent restaurants looking to
                streamline their delivery operations. To join as a partner,
                you’ll need to meet the following criteria:
              </p>
              <ul className="space-y-4 text-sm text-[#666666]">
                <li className="flex items-start gap-3">
                  <FileCheck className="w-6 h-6 text-[#FF5A5F]" />
                  <span>
                    <strong>Legal Business:</strong> You must be a legally
                    operating restaurant with all necessary licenses, permits,
                    and insurance.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Globe className="w-6 h-6 text-[#FF5A5F]" />
                  <span>
                    <strong>Delivery Service Accounts:</strong> Have existing
                    accounts with at least one delivery service (e.g., DoorDash,
                    Uber Eats) or be willing to create them during onboarding.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Users className="w-6 h-6 text-[#FF5A5F]" />
                  <span>
                    <strong>Operational Capacity:</strong> Be able to fulfill
                    delivery orders efficiently, with staff and resources to
                    handle order volumes.
                  </span>
                </li>
              </ul>
            </div>
            <img
              src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&q=80"
              alt="Restaurant interior with staff"
              className="rounded-xl shadow-sm"
              ref={eligibilityImageRef}
            />
          </div>
        </div>
      </section>

      {/* How to Sign Up Section */}
      <section className="px-6 py-32 bg-white max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold mb-12 text-center text-black">
          How to Become a Partner
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              step: "STEP 1",
              title: "Sign Up",
              desc: "Click 'Become a Partner' and fill out a quick form with your restaurant details, including business name, address, and contact information.",
              icon: <UserPlus className="w-6 h-6 text-[#FF5A5F]" />,
            },
            {
              step: "STEP 2",
              title: "Connect Accounts",
              desc: "Link your existing delivery service accounts or let us help you set up new ones. We’ll guide you through the process in minutes.",
              icon: <Globe className="w-6 h-6 text-[#FF5A5F]" />,
            },
            {
              step: "STEP 3",
              title: "Go Live",
              desc: "Upload your menu, set your base prices, and we’ll handle the rest. You’ll be live on all platforms and managing orders within hours.",
              icon: <CheckCircle className="w-6 h-6 text-[#FF5A5F]" />,
            },
          ].map((step, idx) => (
            <div
              key={idx}
              className="bg-[#F5F5F5] p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 text-center"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FF5A5F]/10 mb-4">
                {step.icon}
              </div>
              <p className="text-sm font-semibold text-[#FF5A5F] mb-2">
                {step.step}
              </p>
              <h3 className="text-lg font-semibold mb-2 text-black">
                {step.title}
              </h3>
              <p className="text-sm text-[#666666]">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="px-6 py-32 bg-[#F5F5F5]">
        <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          <img
            src="https://images.unsplash.com/photo-1664575196044-195f135295df?auto=format&fit=crop&w=800&q=80"
            alt="Restaurant dashboard"
            className="rounded-xl shadow-sm"
            ref={expectImageRef}
          />
          <div>
            <h2 className="text-4xl font-bold mb-4 text-black">
              What to Expect After Joining
            </h2>
            <p className="text-[#666666] mb-6">
              Once you become a partner, our team ensures a seamless transition
              to managing your delivery operations:
            </p>
            <ul className="space-y-4 text-sm text-[#666666]">
              <li className="flex items-start gap-3">
                <FileCheck className="w-6 h-6 text-[#FF5A5F]" />
                <span>
                  <strong>Personalized Onboarding:</strong> Our team works with
                  you to set up your dashboard, connect your accounts, and
                  upload your menu—typically within 1-2 hours.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Headphones className="w-6 h-6 text-[#FF5A5F]" />
                <span>
                  <strong>Ongoing Support:</strong> Access AI-driven support for
                  90% of inquiries, plus direct communication with our team for
                  any issues with delivery services.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Clock4 className="w-6 h-6 text-[#FF5A5F]" />
                <span>
                  <strong>Weekly Payouts:</strong> Receive your base menu price
                  earnings via direct deposit every week, with full transparency
                  in your dashboard.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="bg-white px-6 py-32">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center text-black">
            What Our Partners Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote:
                  "We saved $3,800 in commissions the first month and had zero failed deliveries. FullPlateNow is a no-brainer.",
                author: "Thai House, San Diego",
              },
              {
                quote:
                  "Managing orders across platforms used to be a nightmare. Now, it’s all in one place, and we’re seeing 20% more revenue.",
                author: "Pizza Haven, Chicago",
              },
              {
                quote:
                  "The onboarding was seamless, and the analytics help us make smarter decisions every day. Highly recommend!",
                author: "Burger Barn, Austin",
              },
            ].map((testimonial, idx) => (
              <div
                key={idx}
                className="bg-[#F5F5F5] p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 text-center"
              >
                <Star className="w-6 h-6 text-[#FF5A5F] mx-auto mb-4" />
                <blockquote className="text-lg italic text-[#666666] mb-4">
                  “{testimonial.quote}”
                </blockquote>
                <p className="font-semibold text-black">{testimonial.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-32 text-center bg-gradient-to-b from-[#F5F5F5] to-white">
        <h2 className="text-4xl font-bold mb-4 text-black">
          Ready to Simplify Your Delivery?
        </h2>
        <p className="text-lg text-[#666666] mb-8 max-w-xl mx-auto">
          We handle the tech. You handle the food. Let’s partner to grow your
          delivery business.
        </p>
        <Link
          to="/signup"
          className="inline-flex items-center gap-2 text-white bg-[#FF5A5F] px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E04F55] transition-all duration-300 shadow-sm hover:shadow-md"
        >
          Get Started
          <ArrowRight className="w-5 h-5" />
        </Link>
      </section>
    </div>
  );
};

export default BecomePartner;
